'use client';
import { Typography } from 'components';
import { TypographyVariant } from 'components/Typography';
import { motion, MotionProps } from 'framer-motion';
import { cn } from 'utils/cn';

import { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import './styles.scss';

export type SizeButton = 'small' | 'medium' | 'large' | 'x-large';

interface IButton extends MotionProps {
  /**
   * Is this the principal call to action on the page?
   */
  variant?: 'outlined' | 'contained' | 'text-small' | 'text' | 'icon' | 'icon-btn' | 'cta';
  /**
   * How large should the button be?
   */
  size?: SizeButton;
  /**
   * How large should the icon be?
   */
  sizeIcon?: SizeButton;
  /**
   * What color should the button be?
   */
  color?: 'primary' | 'secondary' | 'danger' | 'neutral' | 'white';
  /**
   * What color should the icon be?
   */
  colorIcon?: 'neutral' | 'blue' | 'black' | 'white' | 'danger' | 'none';
  /**
   * Should the button be rounded on border?
   */
  rounded?: boolean;
  /**
   * Classname of Typografy
   */
  classNameTypography?: string;
  /**
   * Button contents
   */
  classNameLoading?: string;
  /**
   * Button contents
   */
  loading?: boolean;
  /**
   * Button hide the label and show the loading
   */
  label?: string;
  /**
   * Icon in the start of button
   */
  startAdornment?: ReactNode;
  /**
   * Icon in the end of button
   */
  endAdornment?: ReactNode;
  /**
   * Button disabled
   */
  disabled?: boolean;
  /**
   * Class name
   */
  className?: string;
  /**
   * Is active ripple?
   */
  isRipple?: boolean;
  /**
   * Button childer
   */
  children?: ReactNode;
  /**
   * Optional click handler
   */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

const Button: FC<IButton> = ({
  children,
  onClick,
  variant = 'contained',
  size = 'large',
  sizeIcon = '',
  label = '',
  color = 'primary',
  colorIcon = '',
  isRipple = true,
  rounded = false,
  disabled = false,
  startAdornment = null,
  endAdornment = null,
  className = '',
  loading = false,
  classNameTypography = '',
  ...rest
}) => {
  const typografyVariants = {
    small: 'medium4',
    medium: 'medium3',
    large: 'medium2'
  } as Record<SizeButton, TypographyVariant>;

  function handleOnClick(event: React.MouseEvent<HTMLButtonElement>) {
    // Optional: If event.detail > 1, ignore double-click or repeated clicks
    if (event.detail > 1) {
      return;
    }

    const button = event.currentTarget;
    if (!button || !isRipple) {
      // If there's a separate callback, call it here
      onClick?.(event);
      return;
    }

    // Remove existing ripple if still in the DOM
    const oldRipple = button.querySelector('.ripple');
    if (oldRipple) {
      oldRipple.remove();
    }

    // Create new ripple
    const circle = document.createElement('span');
    circle.classList.add('ripple');

    const diameter = Math.max(button.offsetWidth, button.offsetHeight);
    const radius = diameter / 2;

    // Use getBoundingClientRect() for a more accurate position
    const rect = button.getBoundingClientRect();
    const top = event.clientY - rect.top - radius;
    const left = event.clientX - rect.left - radius;

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.top = `${top}px`;
    circle.style.left = `${left}px`;

    button.appendChild(circle);

    // Remove ripple after animation
    setTimeout(() => {
      circle.remove();
    }, 600);

    // Call external onClick if defined
    onClick?.(event);
  }

  return (
    <motion.button
      onClick={handleOnClick}
      className={cn(
        'tolq-button',
        `button--${variant}`,
        `button--${size}`,
        disabled
          ? variant !== 'text'
            ? 'button--disabled'
            : 'button--text--disabled'
          : `button--${color}`,
        sizeIcon ? `button--size-icon-${sizeIcon}` : '',
        colorIcon ? `button--color-icon-${colorIcon}` : '',
        rounded ? 'button--rounded' : '',
        className
      )}
      {...rest}
      whileTap={{ scale: variant.includes('icon') ? 0.9 : 0.95 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        restDelta: 10,
        mass: 1
      }}
      disabled={disabled || loading}
    >
      <div className="flex flex-col items-center">
        <div className={cn(loading && 'opacity-0', 'flex items-center gap-2')}>
          {startAdornment}
          {(label || children) && (
            <Typography
              variant={typografyVariants[size]}
              className={cn(classNameTypography, '!transition-none !duration-0')}
            >
              {label}
              {children}
            </Typography>
          )}
          {endAdornment}
        </div>
      </div>
    </motion.button>
  );
};
export default Button;
